import { FunctionComponent, useState } from 'react'
import { cn } from '@pubstack/ui/utils/cn'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'

export interface TabData {
  /** A node, not a string: a tab may carry a dot or a badge alongside its name. */
  label: React.ReactNode
  content: React.ReactNode
  trackingLabel?: TrackingLabel
}

interface TabsProps {
  tabs: TabData[]
  /** Which tab to open on. Read once, at mount — the tabs stay uncontrolled afterwards. */
  initialIndex?: number
  /**
   * Told which tab is showing, for a caller with something of its own to change with it — a flyout whose
   * footer actions belong to one tab, say. The tabs stay uncontrolled: this reports, it does not decide.
   */
  onTabChange?: (index: number) => void
}

/**
 * The tab bar inside a flyout: a tinted track with the selected tab lifted out of it in white.
 *
 * The two radii are deliberately different. A rounded shape sitting inside another one has to curve less
 * than its container, or the gap between the two edges reads as wider at the corners than along the
 * sides — the inner radius is the outer one minus the padding between them. Here that is 12px outside,
 * 4px of track, 8px on the tab itself.
 */
export const CardTabs: FunctionComponent<TabsProps> = ({ tabs, initialIndex = 0, onTabChange }) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex)
  const selectTab = (index: number) => {
    setActiveIndex(index)
    onTabChange?.(index)
  }

  return (
    <div className={'w-full'}>
      <div className={'bg-topaze flex rounded-xl p-1'}>
        {tabs.map((tab, index) => (
          <button
            key={index}
            type={'button'}
            className={cn('h-8 flex-1 cursor-pointer rounded-lg border-none transition-colors', index === activeIndex ? 'text-king bg-white font-bold' : 'text-ink bg-transparent')}
            style={{ flexBasis: `${100 / tabs.length}%` }}
            onClick={() => selectTab(index)}
            data-tracking={tab.trackingLabel}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className={'bg-white p-4'}>{tabs[activeIndex]?.content ?? null}</div>
    </div>
  )
}
